<template>
  <aside
    class="ai-menu-v2"
    :class="{ 'is-settings': activeMenu === 'setting', 'ai-menu-v2--mobile': mobile }"
    :style="{
      width: `${mobile ? 280 : sidebarCollapsed ? 8 : 240}px`
    }"
  >
    <AiSidebarExpandButton
      v-if="
        !mobile &&
        sidebarCollapsed &&
        ['aiBiddingAssistant', 'aiFindCustomerAssistant'].includes(route.name)
      "
      class="ai-menu-v2__expand"
    />
    <header v-if="activeMenu !== 'setting'" class="ai-menu-v2__header">
      <img alt="" class="ai-menu-v2__logo" :src="aiLogo" />
      <span class="ai-menu-v2__title">销帮帮AI CRM</span>
      <button
        v-if="mobile && newTaskMenu"
        :aria-label="newTaskMenu.name"
        class="ai-menu-v2__new-task-button"
        :title="newTaskMenu.name"
        type="button"
        @click="handleRootClick(newTaskMenu)"
      >
        <i aria-hidden="true" class="icon-chat2-ai-line1 t-iconfont"></i>
      </button>
      <button
        v-if="!mobile"
        :aria-label="sidebarCollapsed ? '展开菜单' : '收起菜单'"
        class="ai-menu-v2__collapse"
        type="button"
        @click="sidebarCollapsed = !sidebarCollapsed"
      >
        <i class="icon-layout-left-2-line t-iconfont"></i>
      </button>
    </header>
    <button v-else class="ai-menu-v2__back" type="button" @click="emits('backToMenu')">
      <i aria-hidden="true" class="icon-arrow-left-s-line t-iconfont"></i>
      <span>返回</span>
    </button>
    <div v-if="!mobile && activeMenu !== 'setting' && newTaskMenu" class="ai-menu-v2__new-task">
      <button
        class="ai-menu-v2__item"
        :title="newTaskMenu.name"
        type="button"
        @click="handleRootClick(newTaskMenu)"
      >
        <i class="ai-menu-v2__icon icon-chat2-ai-line1 t-iconfont"></i>
        <span class="ai-menu-v2__text">{{ newTaskMenu.name }}</span>
        <span class="ai-menu-v2__shortcut">
          <span>{{ shortcutModifier }}</span>
          <span style="margin-left: 4px">K</span>
        </span>
      </button>
    </div>
    <SettingsMenu
      v-if="activeMenu === 'setting'"
      :active-alias="activeSubMenu"
      :menu-list="configMenuList"
      @select="handleClickMenu('setting', $event.alias, $event)"
    />
    <el-scrollbar
      v-show="activeMenu !== 'setting'"
      ref="scrollbarRef"
      class="ai-menu-v2__scrollbar"
      :distance="100"
      @end-reached="handleEndReached"
    >
      <nav v-show="activeMenu !== 'setting'" aria-label="主菜单" class="ai-menu-v2__nav">
        <div v-for="item in scrollableMenuList" :key="item.alias" class="ai-menu-v2__group">
          <button
            :aria-expanded="item.subMenuList?.length ? expandedMenu === item.alias : undefined"
            class="ai-menu-v2__item"
            :class="{
              active: activeMenu === item.alias && !item.subMenuList?.length,
              'ai-menu-v2__item--expandable': mobile && item.subMenuList?.length
            }"
            :title="item.name"
            type="button"
            @click="handleRootClick(item)"
          >
            <i
              aria-hidden="true"
              class="ai-menu-v2__icon t-iconfont"
              :class="item.icon || getIconClass(item.alias)"
            ></i>
            <span class="ai-menu-v2__text">{{ item.name }}</span>
            <i
              v-if="mobile && item.subMenuList?.length"
              aria-hidden="true"
              class="ai-menu-v2__expand-icon t-iconfont"
              :class="
                expandedMenu === item.alias ? 'icon-arrow-down-s-line' : 'icon-arrow-right-s-line'
              "
            ></i>
          </button>
          <div
            v-if="item.subMenuList?.length && expandedMenu === item.alias"
            class="ai-menu-v2__children"
          >
            <div
              v-for="child in item.subMenuList"
              :key="child.id || child.alias"
              class="ai-menu-v2__child-group"
            >
              <template v-if="child.subMenuList?.length">
                <div class="ai-menu-v2__section-title">
                  <span>{{ child.name }}</span>
                </div>
                <button
                  v-for="leaf in child.subMenuList"
                  :key="leaf.id || leaf.alias"
                  class="ai-menu-v2__item ai-menu-v2__leaf"
                  :class="{ active: activeSubMenu === leaf.alias }"
                  :title="leaf.name"
                  type="button"
                  @click="handleClickMenu(item.alias, leaf.alias, leaf)"
                >
                  <span class="ai-menu-v2__text">{{ leaf.name }}</span>
                </button>
              </template>
              <button
                v-else
                class="ai-menu-v2__item ai-menu-v2__leaf"
                :class="{
                  active: activeSubMenu === child.alias,
                  'is-agent': item.alias === 'chat',
                  'ai-menu-v2__item--mayi': [
                    'aiFindCustomerAssistant',
                    'aiBiddingAssistant'
                  ].includes(child.alias)
                }"
                :title="child.name"
                type="button"
                @click="handleClickMenu(item.alias, child.alias, child)"
                @contextmenu="handleContextMenu($event, child)"
              >
                <img
                  v-if="item.alias === 'chat'"
                  alt=""
                  class="ai-menu-v2__agent-avatar"
                  :src="agentAvatarMap[child.alias] || defaultAgentAvatar"
                />
                <span class="ai-menu-v2__text">{{ child.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>
      <template v-if="newTaskMenu">
        <RecentConversations
          v-show="activeMenu !== 'setting'"
          ref="recentConversationsRef"
          :mobile="mobile"
          @handleOpenAiAgent="handleOpenAiAgent"
          @navigate="emits('changeActiveMenu', 'aiAgent')"
          @selected="mobile && emits('closeMenu')"
          @updated="fillSessionViewport"
        />
      </template>
    </el-scrollbar>
    <div v-if="activeMenu !== 'setting'" class="ai-menu-v2__bottom">
      <div v-if="mobile" class="ai-menu-v2__user can-click" @click="handleMyClick">
        <img
          v-if="aiLoginUser.url && aiLoginUser.url !== '/images/default.jpg'"
          alt=""
          class="avatar-img"
          :src="aiLoginUser.url"
        />
        <div v-else class="avatar-text">
          <span>{{ showName(aiLoginUser.name) }}</span>
        </div>
        <span class="ai-menu-v2__text">{{ aiLoginUser.name }}</span>
      </div>
      <el-popover
        v-else
        class="person-container"
        placement="top-end"
        popper-class="person-popover"
        trigger="click"
        width="280"
        @show="getCode"
      >
        <div class="person-popover-content">
          <div class="person-popover-content__user">
            <img
              v-if="aiLoginUser.url && aiLoginUser.url !== '/images/default.jpg'"
              alt=""
              class="avatar-img"
              :src="aiLoginUser.url"
            />
            <div v-else class="avatar-text">
              <span>{{ showName(aiLoginUser.name) }}</span>
            </div>

            <div class="person-popover-content__user-name">
              <div class="user-name">{{ aiLoginUser.name }}</div>
              <div class="user-company">{{ userInfo.companyName }}</div>
            </div>
          </div>

          <div class="person-popover-content__setting" @click="handleAuthLogin">
            <i class="icon-error-warning-line t-iconfont"></i>
            <span>授权登录</span>
            <span class="desc">用于问题反馈排查</span>
          </div>

          <div class="person-popover-content__code">
            <div class="item">
              <div class="label">授权码：</div>
              <div class="code">{{ temporaryCode }}</div>
              <i class="icon-refresh-line t-iconfont" @click="getCode"></i>
            </div>
          </div>

          <div class="person-popover-content__code">
            <a
              class="help-link item"
              href="https://xbongbong.oss-cn-hangzhou.aliyuncs.com/cdnfile/html/ai-crm-guide-internal.html"
              rel="noopener noreferrer"
              target="_blank"
            >
              <div class="label">帮助文档</div>
            </a>
          </div>

          <div class="signOut">
            <div class="signOut-text" @click="signOutClick">退出</div>
          </div>
        </div>
        <template #reference>
          <div class="ai-menu-v2__user can-click">
            <img
              v-if="aiLoginUser.url && aiLoginUser.url !== '/images/default.jpg'"
              alt=""
              class="avatar-img"
              :src="aiLoginUser.url"
            />
            <div v-else class="avatar-text">
              <span>{{ showName(aiLoginUser.name) }}</span>
            </div>
            <span class="ai-menu-v2__text">{{ aiLoginUser.name }}</span>
          </div>
        </template>
      </el-popover>
      <div class="ai-menu-v2__actions">
        <button v-if="!mobile" aria-label="返回 CRM" type="button" @click="handleBackXBB">
          <i aria-hidden="true" class="icon-CRM-line t-iconfont"></i>
        </button>
        <button
          v-if="isShowSetting"
          id="ai-menu-setting"
          aria-label="设置"
          :class="{ active: activeMenu === 'setting' }"
          type="button"
          @click="handleOpenSet"
        >
          <i aria-hidden="true" class="icon-settings-4-line t-iconfont"></i>
        </button>
      </div>
    </div>
    <el-tour
      v-if="isShowSetting"
      v-model="open"
      class="ai-menu-tour"
      :gap="{ offset: 6, radius: 8 }"
      :show-arrow="false"
      :show-close="false"
      @close="handleCloseTour"
    >
      <el-tour-step
        :next-button-props="{ children: '知道了' }"
        placement="right-end"
        target="#ai-menu-setting"
      >
        <template #header>
          <div class="ai-menu-tour__title">
            <i class="ai-menu-tour__title-icon icon-sparkling-2-fill t-iconfont"></i>
            <span>初始化配置</span>
          </div>
        </template>
        <div class="ai-menu-tour__description">可以在后台重新唤起初始化</div>
      </el-tour-step>
    </el-tour>

    <el-dialog
      v-model="recordingTipsVisible"
      append-to-body
      class="ai-common-dialog"
      title="取消"
      top="40vh"
      width="400px"
    >
      <span>退出页面将会取消，取消后将不会保存，确定取消吗</span>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="recordingTipsVisible = false">取消</el-button>
          <el-button type="success" @click="confirmClickMenu"> 确认 </el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog
      v-model="authLoginVisible"
      append-to-body
      class="auth-login-dialog"
      title="授权登录"
      top="20vh"
      width="600px"
    >
      <div class="auth-login-content">
        <div class="auth-login-content__tips">
          <div class="title">提示</div>
          <div class="desc">1. 该授权允许开发人员登录您的账户，以方便进行问题排查。</div>
          <div class="desc">
            2.
            开发人员使用临时token进行登录，并不会获取您的账户密码。token有效期为3天，超时后，开发人员将无法登录您的账户。
          </div>
          <div class="desc">3. 开发人员不会泄露您系统中的数据。</div>
        </div>
        <div class="auth-login-content__item">
          <div class="title">是否允许新增数据</div>
          <el-radio-group v-model="addData">
            <el-radio size="large" :value="1">允许</el-radio>
            <el-radio size="large" :value="0">禁止</el-radio>
          </el-radio-group>
          <div class="desc">
            在排查问题时，开发人员可能需新建一些数据来复现BUG，问题解决后，我们将删除这些数据。若您介意我们进行此操作，请选择禁止。
          </div>
        </div>
        <div class="auth-login-content__item">
          <div class="title">问题描述</div>
          <el-input
            v-model="problemDescription"
            placeholder="请简述您所遇到的问题以及重现路径"
            type="textarea"
          />
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="authLoginVisible = false">取消</el-button>
          <el-button :loading="loading" type="primary" @click="confirmAuthLogin"> 确认 </el-button>
        </div>
      </template>
    </el-dialog>
    <Teleport to="body">
      <div
        v-if="contextMenuVisible"
        class="ai-menu-v2__context-menu"
        :style="{ left: `${contextMenuPosition.x}px`, top: `${contextMenuPosition.y}px` }"
        @contextmenu.prevent
      >
        <button type="button" @click="handleForceRefresh(contextMenuItem)">强制刷新页面</button>
      </div>
    </Teleport>
  </aside>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { cancelBackupAudio, endAsr, saveAuthorizationLogin, getTemporaryCode } from '@/api/chat'
import { ElMessage } from 'element-plus'
import AiSidebarExpandButton from '@/components/ai-sidebar-expand-button/index.vue'
import RecentConversations from './components/recent-conversations.vue'
import SettingsMenu from './components/settings-menu.vue'
import { useContextMenu } from './use-contextmenu'
import { useAppStore } from '@/store/useAppStore'
import emitter from '@/utils/mitt'
import utils from '@/utils'
import aiLogo from '@/assets/ai-logo.svg'
import defaultAgentAvatar from '@/assets/ai-avatar/ai-avatar-2x-1.png'
import aiAvatarGJZS from '@/assets/ai-avatar/new-agent/ai-avatar-gjzs.png'
import aiAvatarPLZS from '@/assets/ai-avatar/new-agent/ai-avatar-plzs.png'
import aiAvatarMYZK from '@/assets/ai-avatar/new-agent/ai-avatar-myzk.png'
import aiAvatarMYBX from '@/assets/ai-avatar/new-agent/ai-avatar-mybx.png'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const {
  contextMenuVisible,
  contextMenuItem,
  contextMenuPosition,
  closeContextMenu,
  handleContextMenu
} = useContextMenu()

const handleForceRefresh = (item) => {
  if (!item) return
  closeContextMenu()
  if (['aiFindCustomerAssistant', 'aiBiddingAssistant'].includes(item.alias)) {
    emitter.emit('forceRefreshAiExternalPage', item.alias)
  }
}

defineOptions({
  name: 'AiMenuV2'
})

const emits = defineEmits([
  'backXBB',
  'backToMenu',
  'openSet',
  'signOut',
  'changeActiveMenu',
  'closeMenu'
])

const handleMyClick = () => {
  router.push({ name: 'my' })
}

const props = defineProps({
  mobile: { type: Boolean, default: false },
  activeSubMenu: { type: String, default: '' },
  configMenuList: { type: Array, default: () => [] },
  menuList: {
    type: Array,
    default: () => []
  },
  aiLoginUser: {
    type: Object,
    default: () => ({})
  },
  activeMenu: {
    type: String,
    default: ''
  },
  isShowSetting: {
    type: Boolean,
    default: false
  }
})

const recordingTipsVisible = ref(false)
const open = ref(false)
const currentClickMenuVal = ref()

const userInfo = ref({})
const authLoginVisible = ref(false)
const loading = ref(false)
const addData = ref(1)
const problemDescription = ref('')
const temporaryCode = ref('')

const isMacOS = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)
const shortcutModifier = isMacOS ? '⌘' : 'Ctrl'

const sidebarCollapsed = computed({
  get: () => appStore.sidebarCollapsed,
  set: (value) => appStore.setSidebarCollapsed(value)
})
const expandedMenu = ref('')
const agentAvatarMap = {
  meetingAgent: aiAvatarGJZS,
  aiTrainingAssistant: aiAvatarPLZS,
  aiFindCustomerAssistant: aiAvatarMYZK,
  aiBiddingAssistant: aiAvatarMYBX
}
const aliasEnum = {
  homepage: 'icon-home-5-line',
  aiAgent: 'icon-chat2-ai-line',
  subscribe: 'icon-alarm-clock-line',
  chat: 'icon-chat-smile-ai-line',
  board: 'icon-presentation-ai-line',
  knowledgeBase: 'icon-folder-ai-fill',
  ecoMarket: 'icon-apps2-ai-line',
  extension: 'icon-shapes-line'
}
const getIconClass = (alias) => aliasEnum[alias] || 'icon-chat2-ai-line'
const newTaskMenu = computed(() => props.menuList.find((item) => item.alias === 'aiAgent'))
const scrollableMenuList = computed(() => props.menuList.filter((item) => item.alias !== 'aiAgent'))

watch(
  () => props.activeMenu,
  (alias) => {
    expandedMenu.value = alias
  },
  { immediate: true }
)

const handleRootClick = (item) => {
  if (item.subMenuList?.length) {
    expandedMenu.value = expandedMenu.value === item.alias ? '' : item.alias
    sidebarCollapsed.value = false
    return
  }
  handleClickMenu(item.alias, undefined, item)
}
const scrollbarRef = ref()
const recentConversationsRef = ref()

const refreshFirstPageSessions = () => recentConversationsRef.value?.refresh()

const setActiveMenu = (alias) => {
  expandedMenu.value = alias
}
defineExpose({ refreshFirstPageSessions, setActiveMenu })

const handleEndReached = (direction) => {
  if (props.activeMenu !== 'setting' && direction === 'bottom')
    recentConversationsRef.value?.loadMore()
}

// 首屏不足以产生滚动条时继续补齐，确保仍能获取下一页。
const fillSessionViewport = () => {
  if (props.activeMenu === 'setting') return
  const wrap = scrollbarRef.value?.wrapRef
  if (
    wrap?.clientHeight > 0 &&
    wrap.scrollHeight <= wrap.clientHeight &&
    recentConversationsRef.value?.sessionsList.length === 20
  ) {
    recentConversationsRef.value?.loadMore()
  }
}

watch([sidebarCollapsed, expandedMenu], async () => {
  await nextTick()
  fillSessionViewport()
})

const handleOpenTour = () => {
  open.value = true
}

const handleCloseTour = () => {
  appStore.setIsPopup(0)
}
const handleNewConversation = () => {
  handleClickMenu('aiAgent')
}
// 供AI 初始化菜单使用
emitter.on('openAiMenuTour', handleOpenTour)
// 将新建对话抛出去
emitter.on('handleNewConversation', handleNewConversation)

/**
 * @description 授权登录
 */
const handleAuthLogin = () => {
  authLoginVisible.value = true
}
/**
 * 过滤特殊字符（如控制字符等）
 * @param str 字符串
 * @returns 过滤后的字符串
 */
const CharacterFilter = (str) => {
  let newStr = ''
  for (let i = 0; i < str.length; i++) {
    const us = str.charCodeAt(i)
    if (!(us < 0x20 || us === 0x7f || us === 0x2028 || us === 0x2029)) {
      newStr += str.charAt(i)
    }
  }
  return newStr
}

/**
 * 格式化参数，将对象转为字符串并过滤特殊字符
 * @param data 参数对象
 * @returns 字符串
 */
const formatParams = (params) => {
  let bodyString = JSON.stringify(params)
  if (bodyString.length > 100) {
    bodyString = CharacterFilter(bodyString)
  }
  return bodyString
}

const confirmAuthLogin = () => {
  // authLoginVisible.value = false
  loading.value = true
  const param = {
    userId: userInfo.value.userId,
    corpid: userInfo.value.corpid,
    token: userInfo.value.xbbAccessToken,
    clientInfo: navigator.userAgent,
    corpName: userInfo.value.companyName,
    userName: userInfo.value.userName,
    addData: addData.value,
    problemDescription: problemDescription.value
  }
  // btoa不支持中文转码, 需要编码
  const data = window.btoa(unescape(encodeURIComponent(formatParams(param).trim())))
  saveAuthorizationLogin({
    data
  })
    .then(() => {
      ElMessage.success('操作成功')
      authLoginVisible.value = false
    })
    .catch(() => {})
    .then(() => {
      loading.value = false
    })
}

const confirmClickMenu = () => {
  recordingTipsVisible.value = false
  utils.xbbLocalStorageRemove('isChatbotRecord')
  const asrId = utils.xbbLocalStorageGet('asr_id')
  if (asrId) {
    endAsr({ id: +asrId })
      .then((res) => {
        console.log('结束实时转录', res)
        utils.xbbLocalStorageRemove('asr_id')
      })
      .catch((err) => {
        console.log('结束实时转录失败', err)
      })
  }
  cancelBackupAudio({})
    .then((res) => {
      console.log('取消备份成功', res)
    })
    .catch((err) => {
      console.log('取消备份失败', err)
    })
  handleClickMenu(...currentClickMenuVal.value)
}

const handleOpenAiAgent = () => {
  if (props.mobile) {
    emits('changeActiveMenu', '', '')
    router.push({ name: 'qcaChat' })
    return
  }
  handleClickMenu('aiAgent')
}

const handleClickMenu = (alias, subAlias, item) => {
  if (!props.mobile) {
    if (+utils.xbbLocalStorageGet('isChatbotRecord') === 1 && route.name === 'meetingAgent') {
      recordingTipsVisible.value = true
      currentClickMenuVal.value = [alias, subAlias, item]
      return
    }
    if (item?.url) appStore.setThirdAppKey(item.url)
    if (alias === 'setting') {
      emits('openSet', subAlias)
      return
    }
    if (alias === 'aiAgent' && !subAlias) {
      appStore.setAiSalesMentorSession(null)
      if (route.name === 'aiAgent') emitter.emit('newAiSalesMentorConversation')
    }
    emits('changeActiveMenu', alias, subAlias)
    recentConversationsRef.value?.refresh()
  } else {
    emits('changeActiveMenu', alias, subAlias)
    if (alias === 'aiAgent') {
      appStore.setOpenAiSessionId('')
      appStore.setAiSalesMentorSession(null)
      if (route.name === 'qcaChat') {
        emits('closeMenu')
        emitter.emit('newAiSalesMentorConversation')
      }
      router.push({ name: 'qcaChat' })
      return
    }
    const aliasEnum = {
      subscribe: 'mobile-subscribe', // 定时任务
      meetingAgent: 'mobile-assistant', // 跟进助手
      aiTrainingAssistant: 'mobile-practice-partner', // 陪练助手
      aiScheduleAssistant: 'mobile-schedule-agent', // 日程助手
      meetingAssessment: 'mobilePkBoard', // 会话看板
      conversationQualityReview: 'mobileReviewBoard', // 跟进质量复盘
      meetingReview: 'mobileMeetingReview', // 客户跟进复盘
      customerRedOrGreenLight: 'customerAssetDashboard' // 客户红绿灯
    }
    if (!aliasEnum[item.alias]) return
    router.push({ name: aliasEnum[item.alias] })
  }
}

const showName = (name) => {
  // 展示名字最后一个字
  if (name) {
    return name[name.length - 1]
  }
  return ''
}

const handleBackXBB = () => {
  emits('backXBB')
}

const handleOpenSet = () => {
  emits('openSet')
}

const signOutClick = () => {
  emits('signOut')
}

const getCode = () => {
  const platform = utils.getPlatform(utils.xbbLocalStorageGet('corpid'))
  getTemporaryCode({
    appTag: platform === 'dd' ? 1 : 2
  })
    .then((res) => {
      temporaryCode.value = res.result.temporaryCode
    })
    .catch((err) => {
      console.log(err)
    })
}
const handleGlobalKeydown = (event) => {
  if (!(event.metaKey || event.ctrlKey) || event.repeat || event.key.toLowerCase() !== 'k') return
  if (!props.menuList.some((item) => item.alias === 'aiAgent')) return
  event.preventDefault()
  handleClickMenu('aiAgent')
}
onBeforeUnmount(() => {
  emitter.off('handleNewConversation', handleNewConversation)
  window.removeEventListener('resize', fillSessionViewport)
  emitter.off('openAiMenuTour', handleOpenTour)
  window.removeEventListener('keydown', handleGlobalKeydown)
})
onMounted(() => {
  window.addEventListener('resize', fillSessionViewport)
  window.addEventListener('keydown', handleGlobalKeydown)
  userInfo.value = utils.xbbLocalStorageGet('userInfo') || {}
  getCode()
})
</script>

<style lang="scss" scoped>
.ai-menu-v2__context-menu {
  position: fixed;
  z-index: 10002;
  width: 144px;
  padding: 4px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;

  button {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #323233;
    font-family: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: #f2f3f5;
      }
    }
  }
}

.ai-menu-v2 {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 8px 0;
  background: #eeeeee;
  color: #242832;
  font-family: 'PingFang SC', sans-serif;
  font-size: 13px;
  line-height: 20px;
  transition: width 0.3s ease-in-out;
  &__expand {
    position: absolute;
    top: 18px;
    left: 2px;
    z-index: 10;
    background: #fff !important;
    padding: 6px;
  }
  button {
    font: inherit;
    color: inherit;
    cursor: pointer;
    border: 0;
    background: transparent;
  }
  button:focus-visible {
    outline: 2px solid #7a52f4;
    outline-offset: -2px;
  }
  &__header {
    display: flex;
    align-items: center;
    height: 48px;
    flex-shrink: 0;
    margin-bottom: 12px;
    padding: 0 12px;
  }
  &__title {
    flex: 1;
    padding-left: 12px;
    font-size: 18px;
    font-weight: 600;
    color: #000000;
    white-space: nowrap;
  }
  &__logo {
    display: block;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
  &--mobile {
    padding-top: 0;
    font-size: 14px;
    .ai-menu-v2__header {
      height: 56px;
      padding-left: 24px;
    }
    .ai-menu-v2__title {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ai-menu-v2__item {
      height: 36px;
    }
  }
  &__new-task-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    margin-left: 8px;
    padding: 6px;
    border-radius: 12px;
    background: #ffffff !important;
    color: #242832;
    .t-iconfont {
      font-size: 20px;
      line-height: 20px;
    }
  }
  &__collapse {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 8px;
    flex-shrink: 0;
    .t-iconfont {
      font-size: 16px;
      line-height: 16px;
    }
  }
  &__scrollbar {
    flex: 1;
    height: 0;
    min-height: 0;
    :deep(.el-scrollbar__wrap) {
      padding: 0 12px;
    }
  }
  &__nav {
    padding-top: 4px;
  }
  &__group + &__group {
    margin-top: 4px;
  }
  &__new-task {
    flex-shrink: 0;
    padding: 4px 12px 0 12px;
    margin-bottom: 8px;
    .ai-menu-v2__item {
      background: #ffffff;
      font-weight: 500;
    }
  }
  &__item {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 32px;
    padding: 6px 12px;
    border-radius: 12px;
    text-align: left;
  }
  &__item.active {
    background: #e1e1e1;
  }
  @media (hover: hover) and (pointer: fine) {
    &__item:hover {
      background: #e1e1e1;
    }
  }
  &__item.active {
    font-weight: 500;
  }
  &__icon {
    flex: 0 0 18px;
    width: 18px;
    font-size: 18px;
    line-height: 18px;
    margin-right: 12px;
  }
  &__icon-image {
    flex-shrink: 0;
    margin-right: 12px;
  }
  &__text {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__shortcut {
    margin-left: 12px;
    color: #646d7d;
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
  }
  &__item--expandable > &__text {
    flex: 0 1 auto;
  }
  &__expand-icon {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-left: 12px;
    color: #969799;
    font-size: 16px;
    line-height: 16px;
  }
  &__children {
    position: relative;
    margin-top: 2px;
    padding-left: 32px;
    &::before {
      content: '';
      position: absolute;
      left: 20px;
      top: 8px;
      bottom: 8px;
      border-left: 1px solid #dcdfe6;
    }
  }
  &__child-group + &__child-group {
    margin-top: 8px;
  }
  &__child-group:has(> .is-agent) + &__child-group {
    margin-top: 2px;
  }
  &__section-title {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 6px 12px;
    font-size: 12px !important;
    line-height: 18px !important;
    color: #858e9f !important;
    text-align: left;
  }

  &__leaf {
    margin-top: 2px;
  }
  &__leaf.is-agent {
    margin-top: 0;
  }

  &__agent-avatar {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    margin-right: 12px;
    border-radius: 50%;
  }
  &.is-settings {
    padding: 12px 0;
  }
  &__back {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: 32px;
    margin: 0 12px 12px;
    padding: 6px 12px;
    border-radius: 12px;
    text-align: left;
    color: #858e9f !important;
    font-size: 13px;
    line-height: 20px;
    .t-iconfont {
      margin-right: 12px;
      font-size: 18px;
      line-height: 18px;
    }
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: #e1e1e1;
      }
    }
  }
  &__bottom {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    margin: 12px 12px 0 12px;
    height: 48px;
    box-sizing: border-box;
    border-radius: 16px;
  }
  &__user {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    .avatar-img,
    .avatar-text {
      box-sizing: border-box;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      flex: 0 0 32px;
      border: 1px solid #ffffff;
      margin-right: 8px;
    }
    .avatar-img {
      object-fit: cover;
    }
    .avatar-text {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #5132c6;
      color: #ffffff;
      font-size: 12px;
      font-weight: 500;
    }
    .ai-menu-v2__text {
      font-weight: 500;
    }
    &.can-click {
      cursor: pointer;
    }
  }
  &__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    border-left: 1px solid #e5e6eb;
    margin-left: 12px;
    padding-left: 12px;
    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 4px;
      border-radius: 8px;
    }
    button + button {
      margin-left: 8px;
    }
    i {
      font-size: 20px;
      line-height: 20px;
      color: #646d7d;
    }
    button.active {
      background: #e1e1e1;
    }
    @media (hover: hover) and (pointer: fine) {
      button:hover {
        background: #e1e1e1;
      }
    }
  }
}
:global(.ai-menu-tour .el-tour__content) {
  width: 320px;
  padding: 16px;
  font-family: PingFang SC;
  border-radius: 16px;
}

:global(.ai-menu-tour .el-tour__header) {
  height: 22px;
  padding: 0;
}

:global(.ai-menu-tour .ai-menu-tour__title) {
  display: flex;
  align-items: center;
  height: 22px;
  color: #323233;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

:global(.ai-menu-tour .ai-menu-tour__title-icon) {
  margin-right: 8px;
  width: 18px;
  height: 18px;
  background: linear-gradient(134deg, #1574ff 0%, #5952ff 52%, #a969fe 100%);
  background-clip: text;
  color: transparent;
  font-size: 18px;
  line-height: 18px;
}

:global(.ai-menu-tour .el-tour__body) {
  margin-top: 4px;
  color: #969799;
  font-size: 14px;
  line-height: 20px;
}

:global(.ai-menu-tour .el-tour__footer) {
  justify-content: flex-end;
  padding-top: 16px;
}

:global(.ai-menu-tour .el-tour-indicators) {
  display: none;
}

:global(.ai-menu-tour .el-tour__footer .el-button) {
  width: 52px;
  height: 24px;
  padding: 4px 8px;
  border: 0;
  background: #7a52f4;
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

:global(.ai-menu-tour .el-tour__footer .el-button:focus) {
  background: #7a52f4;
  color: #ffffff;
}
@media (hover: hover) and (pointer: fine) {
  :global(.ai-menu-tour .el-tour__footer .el-button:hover) {
    background: #7a52f4;
    color: #ffffff;
  }
}

.person-popover-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;

  .person-popover-content__user {
    width: 100%;
    display: flex;
    padding-bottom: 8px;
    border-bottom: 1px solid #f2f3f5;
    img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
    }
    .avatar-text {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: #5132c6;
      border: 1px solid #f2f3f5;
      border-radius: 50%;
      span {
        font-family: PingFang SC;
        font-size: 16px;
        font-weight: 600;
        line-height: 16px;
        color: #ffffff;
      }
    }
  }
  .person-popover-content__user-name {
    flex: 1;
    margin-left: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    .user-name {
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #323233;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }
    .user-company {
      font-size: 12px;
      line-height: 16px;
      color: #969799;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .person-popover-content__setting {
    margin-top: 8px;
    border-radius: 8px;
    width: 100%;
    padding: 8px;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    cursor: pointer;
    display: flex;
    align-items: center;
    i {
      margin-right: 8px;
    }
    .desc {
      font-family: PingFang SC;
      font-size: 12px;
      line-height: 16px;
      color: #969799;
      margin-left: 4px;
    }
  }
  .person-popover-content__code {
    width: 100%;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f2f3f5;
    .item {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 8px;
      border-radius: 8px;
      font-size: 14px;
      line-height: 20px;
      color: #323233;
      &.help-link {
        cursor: pointer;
        text-decoration: none;
      }
      .label {
        text-wrap: nowrap;
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background: #f2f3f5;
        }
      }
      .code {
        flex: 1;
      }
      i {
        color: #248df5;
        cursor: pointer;
        flex: 0 0 auto;
      }
    }
  }
  @media (hover: hover) and (pointer: fine) {
    .person-popover-content__setting:hover {
      background: #f2f3f5;
    }
  }

  .signOut {
    border-top: 1px solid #f2f3f5;
    margin-top: 8px;
    padding-top: 8px;
    width: 100%;
    .signOut-text {
      padding: 8px;
      border-radius: 8px;
      color: #f54e4e;
      cursor: pointer;
      padding-top: 8px;
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: #ff8c2e1a;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.person-popover {
  padding: 0 !important;
}
.ai-common-dialog {
  .el-dialog {
    border-radius: 12px !important;
  }
  .el-dialog__header {
    height: 64px !important;
    padding: 0 24px !important;
    background: #fff !important;
    border: none !important;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    .el-dialog__title {
      font-size: 18px !important;
      font-weight: 600 !important;
      line-height: 64px;
      color: #323233 !important;
    }
  }
  .el-dialog__headerbtn {
    top: 24px;
    right: 24px;
    width: 26px;
    height: 26px;
    color: #969799;
  }
  .el-dialog__body {
    padding: 0 24px !important;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
  }
  .el-dialog__footer {
    border: none !important;
    .el-button--success {
      background-color: #7a52f4 !important;
      border-color: #7a52f4 !important;
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: #a871e3 !important;
          border-color: #a871e3 !important;
        }
      }
    }
  }
}
.auth-login-dialog {
  .el-dialog__header {
    padding: 10px;
  }
  .auth-login-content {
    padding: 10px;
    .auth-login-content__tips {
      padding: 20px;
      background: #f0f4fc;
      .title {
        font-size: 14px;
        font-weight: 600;
      }
      .desc {
        line-height: 20px;
        font-size: 12px;
      }
    }
    .auth-login-content__item {
      margin-top: 12px;
      .desc {
        line-height: 20px;
        font-size: 12px;
        padding-left: 20px;
      }
    }
  }
}
</style>
